import { Component, OnInit } from '@angular/core';
import { I18nService, StaticService } from '@localize/services/';
import { AuthService } from '@services/auth/';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback/';
import { ProgressIndicatorService } from '@services/progress/';
import { UserService } from '@services/user/';

import { ObjectString } from '@shared/utilities';
import {
	FormEditorInputType,
	FormValidatorType,
	IFormEditorData,
	IFormEditorRow,
	IFormEditorSelectOption
} from '@app/shared/components';

@Component({
	selector: 'app-manager-account-settings',
	templateUrl: './account-settings.component.html',
	styleUrls: ['./account-settings.component.scss']
})
export class AccountSettingsComponent implements OnInit {
	public userProfileFormData: IFormEditorData;
	public locales: IFormEditorSelectOption[] = [];

	constructor(
		public authService: AuthService,
		private userService: UserService,
		private feedbackService: FeedbackService,
		private progressIndicatorService: ProgressIndicatorService,
		private i18nService: I18nService,
		private staticService: StaticService
	) {}

	public ngOnInit(): void {
		this.locales = this.staticService.get('languages').map((language: { id: any; label: any }) => {
			return {
				value: language.id,
				label: language.label
			};
		});

		this.buildMyProfileCardData();
	}

	public refreshAccounts(): void {
		this.progressIndicatorService.spinner(true);
		this.authService.getAuthUser(true).subscribe(() => this.buildMyProfileCardData());
	}

	public deleteAccount(): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get('Delete account?', 'app.prompt.account.delete.confirm-message'),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.progressIndicatorService.spinner(true);
					this.userService.deleteUser(this.authService.accountUser.id).subscribe(
						(success: boolean) => {
							this.progressIndicatorService.spinner(false);

							success
								? this.authService.logout()
								: this.feedbackService.plain({
										title: FeedbackTitle.attention,
										message: this.i18nService.get(
											'Deletion not possible!',
											'app.prompt.account.delete.blocked-has-associations'
										),
										style: FeedbackStyle.error
								  });
						},
						(error: any) => {
							this.feedbackService.apiError(error);
							this.progressIndicatorService.spinner(false);
						}
					);
				}
			}
		});
	}

	private buildMyProfileCardData() {
		if (this.authService.accountUser) {
			const rows: IFormEditorRow[] = [];

			rows.push({
				title: this.i18nService.get('First name', 'app.descriptor.firstname'),
				key: 'firstname',
				value: this.authService.accountUser.firstname,
				type: FormEditorInputType.text,
				validators: [FormValidatorType.required],
				editable: true
			});

			rows.push({
				title: this.i18nService.get('Last name', 'app.descriptor.lastname'),
				key: 'lastname',
				value: this.authService.accountUser.lastname,
				type: FormEditorInputType.text,
				validators: [FormValidatorType.required],
				editable: true
			});

			rows.push({
				title: this.i18nService.get('Email address', 'app.descriptor.email-address'),
				key: 'email',
				value: this.authService.accountUser.email,
				type: FormEditorInputType.email,
				validators: [FormValidatorType.required, FormValidatorType.uniq_email],
				valids: [this.authService.accountUser.email],
				editable: false,
				icon: 'lock'
			});

			rows.push({
				title: this.i18nService.get('Language', 'app.descriptor.language'),
				key: 'locale',
				value: this.authService.accountUser.locale,
				type: FormEditorInputType.select,
				validators: [FormValidatorType.required],
				editable: true,
				options: this.locales
			});

			this.userProfileFormData = {
				title: this.i18nService.get('My profile', 'app.descriptor.my-profile'),
				rows: rows,
				callback: (rows2: IFormEditorRow[]) => {
					this.saveNewUserSettings(rows2);
				}
			};

			this.progressIndicatorService.spinner(false);
		}
	}

	private saveNewUserSettings(updatedRows: IFormEditorRow[]) {
		this.progressIndicatorService.spinner(true);

		const params: ObjectString<any> = {};
		updatedRows.forEach((element) => {
			params[element.key] = element.newValue;
		});

		this.userService.updateUser(params).subscribe(
			() => {
				this.buildMyProfileCardData();
				this.progressIndicatorService.spinner(false);
			},
			(error: any) => {
				this.feedbackService.apiError(error);
				this.progressIndicatorService.spinner(false);
			}
		);
	}
}
